import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { UserSelect } from '@/components/ui/user-select'
import { Modal } from '@/components/ui/modal'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { apiClient } from '@/lib/api-client'
import { toast } from 'react-hot-toast'

interface AssignmentModalProps {
  isOpen: boolean
  onClose: () => void
  deviceId: number
  deviceName: string
  currentAssignment?: any
  onSuccess: () => void
}

export function AssignmentModal({ 
  isOpen, 
  onClose, 
  deviceId, 
  deviceName, 
  currentAssignment,
  onSuccess 
}: AssignmentModalProps) {
  const [selectedUserId, setSelectedUserId] = useState('')
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)

  const queryClient = useQueryClient()

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedUserId('')
      setNotes('')
    }
  }, [isOpen])

  const assignmentMutation = useMutation({
    mutationFn: async (data: { userId: number; notes?: string }) => {
      if (currentAssignment) {
        // Reassign - first return current assignment, then assign to new user
        await apiClient.put(`/assignments/${currentAssignment.id}/return`, {
          notes: 'Reassigned to another user'
        })
      }
      
      // Create new assignment
      await apiClient.post('/assignments', {
        deviceId,
        employeeId: data.userId,
        notes: data.notes
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['device', deviceId] })
      queryClient.invalidateQueries({ queryKey: ['assignments'] })
      toast.success(currentAssignment ? 'Asset reassigned' : 'Asset assigned')
      onSuccess()
      onClose()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to assign device')
    }
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedUserId) return

    setLoading(true)
    try {
      await assignmentMutation.mutateAsync({
        userId: parseInt(selectedUserId),
        notes: notes.trim() || undefined
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size="md"
      title={currentAssignment ? 'Reassign asset' : 'Assign asset'}
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            {currentAssignment ? 'Reassign' : 'Assign'} <strong>{deviceName}</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="user">Select User</Label>
            <UserSelect
              value={selectedUserId}
              onChange={setSelectedUserId}
              placeholder="Search for a user to assign the device to..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Assignment Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this assignment..."
              rows={3}
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={!selectedUserId || loading || assignmentMutation.isPending}
            >
              {loading || assignmentMutation.isPending ? (
                <LoadingSpinner size="sm" className="mr-2" />
              ) : (
                <User className="mr-2 h-4 w-4" />
              )}
              {currentAssignment ? 'Reassign asset' : 'Assign asset'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  )
}
